import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Menu,
  X,
  Home,
  ShoppingCart,
  Users,
  Settings,
  User,
  LogOut,
  Bell,
  FileText,
  CreditCard,
  Database,
  Building,
  Calendar,
  Package,
  Truck,
  Calculator,
  Package2,
  QrCode,
  TrendingUp,
  MessageSquare,
  Book,
  ChevronDown,
  ChevronRight,
  Coin,
  Coins,
  Landmark,
  Camera,
  UserCheck,
  ClipboardList,
  Star,
  BarChart2
} from 'lucide-react';
import NotificationSystem from './NotificationSystem';
import AlertBadge from './AlertBadge';
import RoleNavigation from './RoleNavigation';

const BancolombiaIcon = ({ className }) => (
  <div className={`${className} flex items-center justify-center bg-yellow-400 rounded-md text-black shadow-sm`}>
    <Landmark size={16} />
  </div>
);

const Layout = () => {
  const { user, logout, hasPermission, getRoleName } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  const [businessControlsOpen, setBusinessControlsOpen] = useState(false);
  const [attendanceMenuOpen, setAttendanceMenuOpen] = useState(false);
  const [marketingMenuOpen, setMarketingMenuOpen] = useState(false);
  const [clientsMenuOpen, setClientsMenuOpen] = useState(false);
  const [usersMenuOpen, setUsersMenuOpen] = useState(false);
  const [billingMenuOpen, setBillingMenuOpen] = useState(false);
  const location = useLocation();
  const isInventoryBilling = location.pathname === '/inventory-billing' || location.pathname.startsWith('/inventory-billing');
  
  const getGroupState = (groupName) => {
    if (groupName === 'Configuración') return configMenuOpen;
    if (groupName === 'Controles Empresariales') return businessControlsOpen;
    if (groupName === 'Usuarios') return usersMenuOpen;
    if (groupName === 'Marketing') return marketingMenuOpen;
    if (groupName === 'Clientes') return clientsMenuOpen;
    if (groupName === 'Facturación') return billingMenuOpen;
    return false;
  };

  const getToggleFunc = (groupName) => {
    if (groupName === 'Configuración') return setConfigMenuOpen;
    if (groupName === 'Controles Empresariales') return setBusinessControlsOpen;
    if (groupName === 'Usuarios') return setUsersMenuOpen;
    if (groupName === 'Marketing') return setMarketingMenuOpen;
    if (groupName === 'Clientes') return setClientsMenuOpen;
    if (groupName === 'Facturación') return setBillingMenuOpen;
    return null;
  };

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      current: location.pathname === '/dashboard',
      requiresPermission: ['admin', 'facturador', 'facturacion', 'cartera', 'gerente'],
    },
    {
      name: 'Dashboard Ejecutivo',
      href: '/admin/dashboard',
      icon: TrendingUp,
      current: location.pathname === '/admin/dashboard',
      requiresPermission: ['admin', 'gerente'],
    },
    {
      name: 'Pedidos',
      href: '/orders',
      icon: ShoppingCart,
      current: location.pathname === '/orders',
      requiresPermission: ['admin', 'facturador', 'facturacion', 'cartera', 'gerente', 'logistica', 'empaque', 'empacador'],
    },
    {
      name: 'Productos',
      href: '/products',
      icon: Package,
      current: location.pathname === '/products',
      requiresPermission: ['admin', 'logistica'],
    },
    {
      name: 'Códigos Proveedor',
      href: '/supplier-codes',
      icon: Database,
      current: location.pathname === '/supplier-codes',
      requiresPermission: ['admin', 'logistica'],
    },
    {
      name: 'Facturación',
      icon: FileText,
      requiresPermission: ['admin', 'facturador', 'facturacion'],
      isGroup: true,
      children: [
        {
          name: 'Pedidos por Facturar',
          href: '/orders?view=facturacion&status=pendiente_por_facturacion',
          icon: ShoppingCart,
          current: location.pathname.includes('view=facturacion'),
        },
        {
          name: 'Cierre e Historial',
          href: '/orders?view=historial_facturacion',
          icon: History,
          current: location.pathname.includes('view=historial_facturacion'),
        },
        {
          name: 'Gestión de Clientes',
          href: '/customers',
          icon: Users,
          current: location.pathname === '/customers',
        },
        {
          name: 'Campaña Aniversario',
          href: '/anniversary-campaign',
          icon: Star,
          current: location.pathname === '/anniversary-campaign',
        },
        {
          name: 'Punto de Venta (POS)',
          href: '/inventory-billing',
          icon: Package2,
          current: location.pathname === '/inventory-billing',
        },
        {
          name: 'Facturas SIIGO',
          href: '/siigo-invoices',
          icon: FileText,
          current: location.pathname === '/siigo-invoices',
        },
        {
          name: 'Cotizaciones',
          href: '/quotations',
          icon: Calculator,
          current: location.pathname === '/quotations',
        },
      ]
    },
    {
      name: 'Usuarios',
      icon: Users,
      requiresPermission: 'admin',
      isGroup: true,
      children: [
        {
          name: 'Gestión Usuarios',
          href: '/users',
          icon: User,
          current: location.pathname === '/users',
        },
        {
          name: 'Panel Asistencia',
          href: '/admin/attendance',
          icon: UserCheck,
          current: location.pathname === '/admin/attendance',
        },
        {
          name: 'Modo Kiosco',
          href: '/attendance',
          icon: Camera,
          current: location.pathname === '/attendance',
        },
      ]
    },
    {
      name: 'Asistencia',
      href: '/attendance',
      icon: Camera,
      current: location.pathname === '/attendance',
      requiresPermission: ['gerente', 'cartera', 'logistica', 'facturador', 'empaque', 'empacador'],
    },
    {
      name: 'Marketing',
      icon: TrendingUp,
      requiresPermission: ['admin', 'gerente'],
      isGroup: true,
      children: [
        {
          name: 'Analytics Social',
          href: '/marketing-analytics',
          icon: BarChart2,
          current: location.pathname === '/marketing-analytics',
        },
      ]
    },
    // Configuración submenu group
    {
      name: 'Configuración',
      icon: Settings,
      requiresPermission: 'admin',
      isGroup: true,
      children: [
        {
          name: 'Config Empresa',
          href: '/company-config',
          icon: Building,
          current: location.pathname === '/company-config',
        },
        {
          name: 'API Config',
          href: '/api-config',
          icon: Settings,
          current: location.pathname === '/api-config',
        },
        {
          name: 'Config Bancolombia',
          href: '/bank-config',
          icon: Building,
          current: location.pathname === '/bank-config',
        },
        {
          name: 'Automatización',
          href: '/automation',
          icon: Settings,
          current: location.pathname === '/automation',
        },
        {
          name: 'Fecha Inicio SIIGO',
          href: '/siigo-start-date-config',
          icon: Calendar,
          current: location.pathname === '/siigo-start-date-config',
        },
        {
          name: 'Crédito de Clientes',
          href: '/customer-credit',
          icon: CreditCard,
          current: location.pathname === '/customer-credit',
        },
        {
          name: 'Métodos de Envío',
          href: '/delivery-methods',
          icon: Settings,
          current: location.pathname === '/delivery-methods',
        },
        {
          name: 'Transportadoras',
          href: '/carriers',
          icon: Truck,
          current: location.pathname === '/carriers',
        },
      ]
    },
    {
      name: 'Gestión de Inventario',
      href: '/inventory-management',
      icon: Database,
      current: location.pathname === '/inventory-management',
      requiresPermission: ['admin', 'facturador', 'cartera', 'logistica'],
    },
    {
      name: 'Recepción Mercancía',
      href: '/reception',
      icon: Package,
      current: location.pathname === '/reception',
      requiresPermission: ['admin', 'facturador', 'facturacion', 'cartera', 'logistica', 'empaque', 'packaging'],
    },
    {
      name: 'PQR',
      href: '/pqr',
      icon: ClipboardList,
      current: location.pathname === '/pqr',
      requiresPermission: ['admin', 'cartera', 'facturador', 'facturacion', 'logistica'],
    },
    {
      name: 'Encuestas',
      href: '/encuestas',
      icon: Star,
      current: location.pathname === '/encuestas',
      requiresPermission: ['admin', 'gerente'],
    },
    {
      name: 'Generador QR',
      href: '/qr-generator',
      icon: QrCode,
      current: location.pathname === '/qr-generator',
      requiresPermission: ['admin', 'logistica'],
    },

    // Controles Empresariales group
    {
      name: 'Controles Empresariales',
      icon: TrendingUp,
      requiresPermission: ['admin', 'cartera', 'facturador', 'facturacion'],
      isGroup: true,
      children: [
        {
          name: 'Cierre Financiero',
          href: '/financial-closure',
          icon: Calculator,
          current: location.pathname === '/financial-closure',
        },
        {
          name: 'Control Operativo',
          href: '/operational-metrics',
          icon: MessageSquare,
          current: location.pathname === '/operational-metrics',
        },
        {
          name: 'Control de Egresos',
          href: '/expenses',
          icon: CreditCard,
          current: location.pathname === '/expenses',
        },
      ]
    },
    {
      name: 'Pagos Bancolombia',
      href: '/bank-money-control',
      icon: BancolombiaIcon,
      current: location.pathname === '/bank-money-control',
      requiresPermission: ['admin', 'cartera'],
    },
    {
      name: 'Alertas de Auditoría',
      href: '/alerts',
      icon: Bell,
      current: location.pathname === '/alerts',
      requiresPermission: 'admin',
    },
    {
      name: 'Manual de Usuario',
      href: '/manual',
      icon: Book,
      current: location.pathname === '/manual',
    },
  ];

  const handleLogout = () => {
    logout();
  };

  const filteredNavigation = navigation.filter(item =>
    !item.requiresPermission || hasPermission(item.requiresPermission)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar para móvil */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
        <div
          className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ${sidebarOpen ? 'opacity-100' : 'opacity-0'
            }`}
          onClick={() => setSidebarOpen(false)}
        />

        <div
          className={`relative flex-1 flex flex-col max-w-xs w-full bg-white transform transition-transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
        >
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>

          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <ShoppingCart className="h-5 w-5 text-white" />
              </div>
              <span className="ml-2 text-xl font-bold text-gray-900">
                Gestión Pedidos
              </span>
            </div>

            <nav className="mt-5 px-2 space-y-1">
              {filteredNavigation.map((item) => {
                const Icon = item.icon;

                if (item.isGroup) {
                  const isOpen = getGroupState(item.name);
                  const toggleFunc = getToggleFunc(item.name);

                  return (
                    <div key={item.name}>
                      <button
                        onClick={() => toggleFunc && toggleFunc(!isOpen)}
                        className="group w-full flex items-center px-2 py-2 text-base font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      >
                        <Icon className="mr-4 h-6 w-6" />
                        <span className="flex-1 text-left">{item.name}</span>
                        {isOpen ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronRight className="h-5 w-5" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children.map((child) => {
                            const ChildIcon = child.icon;
                            return (
                              <Link
                                key={child.name}
                                to={child.href}
                                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${child.current
                                  ? 'bg-primary-100 text-primary-900'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                  }`}
                                onClick={() => setSidebarOpen(false)}
                              >
                                <ChildIcon className="mr-3 h-5 w-5" />
                                {child.name}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${item.current
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    {item.customIcon ? (
                      <img src={item.customIcon} alt="" className="mr-4 h-6 w-6 object-contain rounded" />
                    ) : (
                      <Icon className="mr-4 h-6 w-6" />
                    )}
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="h-10 w-10 bg-gray-300 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-base font-medium text-gray-700">{user?.full_name}</p>
                <p className="text-sm font-medium text-gray-500">{getRoleName(user?.role)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar para desktop */}
      {!isInventoryBilling && (
        <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
          <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="h-5 w-5 text-white" />
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900">
                  Gestión Pedidos
                </span>
              </div>

              <nav className="mt-5 flex-1 px-2 space-y-1">
                {filteredNavigation.map((item) => {
                  const Icon = item.icon;

                  if (item.isGroup) {
                    const isConfigGroup = item.name === 'Configuración';
                    const isBusinessGroup = item.name === 'Controles Empresariales';
                    const isUsersGroup = item.name === 'Usuarios';
                    const isClientsGroup = item.name === 'Clientes';
                    const isOpen = isConfigGroup ? configMenuOpen : (isBusinessGroup ? businessControlsOpen : (isUsersGroup ? usersMenuOpen : (isClientsGroup ? clientsMenuOpen : false)));
                    const toggleFunc = isConfigGroup ? setConfigMenuOpen : (isBusinessGroup ? setBusinessControlsOpen : (isUsersGroup ? setUsersMenuOpen : (isClientsGroup ? setClientsMenuOpen : null)));

                    return (
                      <div key={item.name}>
                        <button
                          onClick={() => toggleFunc && toggleFunc(!isOpen)}
                          className="group w-full flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        >
                          <Icon className="mr-3 h-6 w-6" />
                          <span className="flex-1 text-left">{item.name}</span>
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="ml-4 mt-1 space-y-1">
                            {item.children.map((child) => {
                              const ChildIcon = child.icon;
                              return (
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${child.current
                                    ? 'bg-primary-100 text-primary-900'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                >
                                  <ChildIcon className="mr-3 h-5 w-5" />
                                  {child.name}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${item.current
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                    >
                      {item.customIcon ? (
                        <img src={item.customIcon} alt="" className="mr-3 h-6 w-6 object-contain rounded" />
                      ) : (
                        <Icon className="mr-3 h-6 w-6" />
                      )}
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <Link to="/profile" className="flex-shrink-0 w-full group block">
                <div className="flex items-center">
                  <div className="h-9 w-9 bg-gray-300 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      {user?.full_name}
                    </p>
                    <p className="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                      {getRoleName(user?.role)}
                    </p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Contenido principal */}
      <div className={`${isInventoryBilling ? '' : 'md:pl-64'} flex flex-col flex-1 min-w-0`}>

        {/* Navegación por roles - Ahora es la barra superior principal */}
        <RoleNavigation setSidebarOpen={setSidebarOpen} onLogout={handleLogout} />

        {/* Contenido de la página */}
        <main className="flex-1 pt-12 text-sm">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
